import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { useSyncSizeStore, tooLargeInScope } from '../records/syncSizeStore';
import './SyncTooLargeBanner.css';

const kb = (bytes) => `${Math.ceil(bytes / 1000)} KB`;

/**
 * Persistent warning for a document or board whose latest save is too large
 * to sync (see `records/syncSizeStore.js`). Stays until a save fits again —
 * there is deliberately no dismiss: while it shows, the other devices and the
 * team are looking at an older version.
 *
 * `scopeId` is the document id or the board id; `kind` picks the wording.
 */
export default function SyncTooLargeBanner({ scopeId, kind = 'document' }) {
  const { t } = useTranslation();
  const tooLarge = useSyncSizeStore((s) => s.tooLarge);
  const entries = React.useMemo(() => tooLargeInScope(tooLarge, scopeId), [tooLarge, scopeId]);
  if (!scopeId || entries.length === 0) return null;

  const largest = entries[0];
  // The bar spans the record's size; the part past the limit is what has to go.
  const withinLimit = largest.bytes > 0 ? Math.min(100, Math.round((largest.cap / largest.bytes) * 100)) : 100;
  const title =
    kind === 'board' ? t('syncTooLarge.boardTitle', { count: entries.length }) : t('syncTooLarge.documentTitle');
  const body = kind === 'board' ? t('syncTooLarge.boardBody') : t('syncTooLarge.documentBody');

  return (
    <div className="sync-too-large-banner" role="alert" data-testid="sync-too-large-banner">
      <AlertTriangle size={15} className="sync-too-large-icon" aria-hidden />
      <div className="sync-too-large-text">
        <strong>{title}</strong>
        <span>{body}</span>
      </div>
      <div className="sync-too-large-meter-wrap">
        <div
          className="sync-too-large-meter"
          role="meter"
          aria-label={t('syncTooLarge.meterLabel')}
          aria-valuemin={0}
          aria-valuemax={largest.cap}
          aria-valuenow={largest.bytes}
        >
          <div className="sync-too-large-meter-fill" style={{ width: `${withinLimit}%` }} />
          <div className="sync-too-large-meter-over" style={{ width: `${100 - withinLimit}%` }} />
        </div>
        <span className="sync-too-large-meter-text">
          {t('syncTooLarge.meter', { size: kb(largest.bytes), cap: kb(largest.cap) })}
        </span>
      </div>
    </div>
  );
}
